/**
 * How an area on the map looks, as the game draws it. The table of meanings
 * and looks is the platform's (`MapMarks`, `docs/design/DYNAMIC_MISSIONS_UI.md`
 * "The map's marks"): the definition arrives with each shape's `brush`,
 * `colour` and `labelEdge` already stamped, and this module only turns them
 * into what an SVG can draw.
 *
 * Everything here is pure and unit-tested (mapMarks.test.ts): a polygon's
 * middle and reach, a sector's letter, how large a label is at a zoom, and
 * which labels move or hide so that none is written over another — the one
 * rule the runtime applies in game (`fn_marksArrange`), ported so the web's
 * map and the players' map agree.
 */

import type { WorldBounds } from './mapView';

/**
 * The box a shape of the definition stands in, in world metres: a polygon's
 * corners; a rectangle's or an ellipse's half-sides round its centre, turned
 * by its angle (its diagonal when it is turned, so the box always holds it).
 * Null for a shape with nothing to frame.
 */
export function shapeBounds(shape: {
  shape: string;
  center?: number[];
  position?: number[];
  a?: number;
  b?: number;
  angle?: number;
  points?: number[][];
}): WorldBounds | null {
  if (shape.shape === 'polygon') return polygonBounds(shape.points);
  const centre = shape.center ?? shape.position ?? [];
  const a = shape.a ?? 0;
  const b = shape.b ?? 0;
  if (centre.length < 2 || !(a > 0) || !(b > 0)) return null;
  const square = Math.abs(((shape.angle ?? 0) % 180) + 180) % 180 < 1e-6;
  const rx = square ? a : Math.hypot(a, b);
  const ry = square ? b : Math.hypot(a, b);
  return { minX: centre[0] - rx, minY: centre[1] - ry, maxX: centre[0] + rx, maxY: centre[1] + ry };
}

/**
 * What a map framed on the area of operations opens on: the area's box, grown
 * to hold every objective the map draws with its circle, but the way home
 * (`exfiltrate`, `evade_to`), which stands outside the area on purpose. A
 * mission generated before the area was fitted to its contents (2026-10-11)
 * had its objective 1.5 km south of a thin box, and the briefing opened on the
 * box with the objective cut off at its foot (the owner, test card 160,
 * 2026-10-17). Null with no area.
 */
export function areaFrame(
  area: Parameters<typeof shapeBounds>[0] | undefined,
  objectives: { type: string; position: number[]; radius?: number }[] = [],
): WorldBounds | null {
  const box = area ? shapeBounds(area) : null;
  if (!box) return null;
  const out = { ...box };
  for (const o of objectives) {
    if (o.type === 'exfiltrate' || o.type === 'evade_to' || !(o.position?.length >= 2)) continue;
    const r = Math.max(0, o.radius ?? 0);
    out.minX = Math.min(out.minX, o.position[0] - r);
    out.maxX = Math.max(out.maxX, o.position[0] + r);
    out.minY = Math.min(out.minY, o.position[1] - r);
    out.maxY = Math.max(out.maxY, o.position[1] + r);
  }
  return out;
}

/** A polygon's bounding box in world metres; null for fewer than three points. */
export function polygonBounds(
  points: number[][] | undefined,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const p = (points ?? []).filter((v) => Array.isArray(v) && v.length >= 2);
  if (p.length < 3) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of p) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { minX, minY, maxX, maxY };
}

/**
 * A polygon's centroid, weighted by area, so a block that is wide at one end
 * is labelled where most of it is. A degenerate polygon (no area) answers the
 * mean of its vertices; fewer than three points answer null.
 */
export function polygonCentroid(points: number[][] | undefined): [number, number] | null {
  const p = (points ?? []).filter((v) => Array.isArray(v) && v.length >= 2);
  if (p.length < 3) return null;
  let twiceArea = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < p.length; i++) {
    const [x0, y0] = p[i];
    const [x1, y1] = p[(i + 1) % p.length];
    const cross = x0 * y1 - x1 * y0;
    twiceArea += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  if (Math.abs(twiceArea) < 1e-9) {
    return [p.reduce((s, v) => s + v[0], 0) / p.length, p.reduce((s, v) => s + v[1], 0) / p.length];
  }
  return [cx / (3 * twiceArea), cy / (3 * twiceArea)];
}

/**
 * A sector's text, as the generator writes it on a `polygon` marker: its
 * letter, then what else the sector is after a middle dot («C · reducte»).
 * The letter is what stands inside the sector; the rest is said beside it
 * only with the map close enough to read.
 */
export function sectorWords(text: string | undefined): { letter: string; note: string } {
  const [letter = '', ...rest] = (text ?? '').split(/\s*·\s*/);
  return { letter: letter.trim(), note: rest.join(' · ').trim() };
}

/**
 * How many canvas units a label drawn in screen pixels takes: the canvas is
 * scaled by the view, so a label counter-scaled by this keeps its size on the
 * screen however close the map is, and shrinks with the map only when the
 * whole terrain is on screen (never larger than drawn).
 */
export function labelScale(viewScale: number): number {
  return 1 / Math.max(1, viewScale);
}

/** How many screen pixels `metres` take at a zoom, on a square of `size` pixels for `worldSize` metres. */
export function spanOnScreen(
  metres: number,
  viewScale: number,
  size: number,
  worldSize: number,
): number {
  if (!(worldSize > 0) || !(size > 0)) return 0;
  return (metres / worldSize) * size * viewScale;
}

/** A shape's label is worth drawing once the shape itself is this many pixels across. */
export const LABEL_SPAN_PX = 28;
/**
 * A sector's letter is drawn once its sector is this many pixels across: about
 * its disc's own width, so the letters of neighbouring sectors stand on their
 * sectors instead of on each other. A letter never moves off its sector (it was
 * pushed a line north or south, and the briefing drew A, B and C 50 px apart
 * over three sectors 10 px apart: the owner, test card 159, 2026-10-17).
 */
export const SECTOR_SPAN_PX = 22;
/** A sector's note («reducte») beside its letter, once the sector is this wide. */
export const SECTOR_NOTE_SPAN_PX = 80;

export interface ArrangeMark {
  id: string;
  x: number;
  y: number;
  /** The text's length in characters. */
  len: number;
  /** 0 is an anchor that never moves; higher ranks give way to lower ones. */
  rank: number;
  /** How many lines this one may go, when it may go fewer than the call's `max`. */
  max?: number;
}

export interface ArrangedMark {
  id: string;
  x: number;
  y: number;
  how: 'kept' | 'moved' | 'merged';
  /** The id of the mark a merged one joins; empty otherwise. */
  into: string;
}

/**
 * Marks whose texts would be written over each other, arranged so each reads:
 * the runtime's `fn_marksArrange`, the same rule. A mark is a box — its icon,
 * then its text to the right, a line high. An anchor (rank 0) stays where it
 * is whatever it crosses. Every other mark, lower ranks first and then in the
 * order given, stays when its box crosses no box already placed; else it goes
 * the fewest whole lines north or south (north first) that clear it, its east
 * unchanged, so a stack reads as a column. One with no free line within
 * `max` lines either way is merged: not drawn, its text joining the mark it
 * crossed first.
 *
 * `line` is a text line's height and `char` a character's width, in the same
 * units as the positions (metres in game, screen pixels here); `up` is the
 * sign of north on the y axis (+1 for world metres, -1 for screen pixels).
 */
export function arrangeMarks(
  marks: ArrangeMark[],
  line: number,
  { max = 3, char = 0, up = 1 }: { max?: number; char?: number; up?: number } = {},
): ArrangedMark[] {
  const lineH = line > 0 ? line : 100;
  const charW = char > 0 ? char : lineH * 0.55;
  const north = up < 0 ? -1 : 1;
  type Box = { west: number; east: number; south: number; north: number; id: string };
  const boxOf = (id: string, x: number, y: number, len: number): Box => ({
    west: x - lineH / 2,
    east: x + lineH / 2 + Math.max(0, len) * charW,
    south: y - lineH / 2,
    north: y + lineH / 2,
    id,
  });
  const placed: Box[] = [];
  const crossed = (b: Box): string => {
    for (const p of placed) {
      if (b.west < p.east && p.west < b.east && b.south < p.north && p.south < b.north) return p.id;
    }
    return '';
  };
  const ranks = [...new Set(marks.map((m) => m.rank ?? 1))].sort((a, b) => a - b);
  const answer = new Map<string, ArrangedMark>();
  for (const rank of ranks) {
    for (const m of marks) {
      if ((m.rank ?? 1) !== rank) continue;
      const first = rank <= 0 ? '' : crossed(boxOf(m.id, m.x, m.y, m.len));
      if (first === '') {
        placed.push(boxOf(m.id, m.x, m.y, m.len));
        answer.set(m.id, { id: m.id, x: m.x, y: m.y, how: 'kept', into: '' });
        continue;
      }
      let done = false;
      for (let k = 1; k <= Math.min(max, m.max ?? max) && !done; k++) {
        for (const dir of [1, -1]) {
          if (done) break;
          const y2 = m.y + dir * north * k * lineH;
          const box = boxOf(m.id, m.x, y2, m.len);
          if (crossed(box) === '') {
            placed.push(box);
            answer.set(m.id, { id: m.id, x: m.x, y: y2, how: 'moved', into: '' });
            done = true;
          }
        }
      }
      if (!done) answer.set(m.id, { id: m.id, x: m.x, y: m.y, how: 'merged', into: first });
    }
  }
  return marks.map(
    (m) => answer.get(m.id) ?? { id: m.id, x: m.x, y: m.y, how: 'kept' as const, into: '' },
  );
}

/**
 * A sector's hatch on the web's map, as opacities of its holder's colour: the
 * ground under the whole sector, and the stripe drawn over half of it. A
 * stripe ends at 1 - (1 - ground)(1 - stripe) = 0.29, the game's 0.3
 * (`fn_markerDraw`, `ClearAreaScript.SECTOR_ALPHA`). They were 0.14 and 0.5 (a
 * stripe at 0.57, the game's old 0.6), which hid the houses under a sector
 * (the owner, test card 105, 2026-10-12). Who holds a sector is told by its
 * colour, never by how opaque it is.
 */
export const SECTOR_HATCH = { ground: 0.06, stripe: 0.24 } as const;

/** What an objective's area is called when its objective's kind has no name of its own. */
export const OBJECTIVE_AREA = 'Zona de l’objectiu';

/**
 * What an objective's area is called on the map, by the kind of its
 * objective: what the area is for the players, never where it is. The
 * platform's table (`MapMarks.areaName`).
 */
export function areaName(objectiveType: string | undefined): string {
  switch (objectiveType) {
    case 'ambush_convoy':
    case 'ambush_reaction':
      return 'Zona de l’emboscada';
    case 'clear_area':
    case 'seize_outpost':
      return 'Zona a netejar';
    case 'defend_position':
      return 'Zona a defensar';
    case 'observe_position':
      return 'Zona a observar';
    case 'rescue_hostage':
      return 'Zona del rescat';
    case 'capture_hvt':
      return 'Zona de la captura';
    case 'sabotage_vehicles':
      return 'Zona del sabotatge';
    case 'find_cache':
    case 'retrieve_intel':
      return 'Zona de recerca';
    default:
      return OBJECTIVE_AREA;
  }
}

/** What of a definition says whether a label is a place's name, and what an area is for. */
export interface LabelContext {
  objectives?: { id: string; type: string; title?: string; priority?: string; silent?: boolean }[];
  markers?: { id: string; text?: string }[];
  civilians?: { label?: string }[];
  zones?: { label?: string }[];
}

/**
 * Whether a label is the bare name of a place, read from the definition
 * alone: another text of it says the same words inside a sentence or a longer
 * label — an objective's title or its mark's («Embosca la columna prop del
 * castell» holds «el castell»), a civil zone named after its town, a zone's
 * «Zona civil: <town>». A label that already says what its shape is («Zona
 * …», «Objectiu») names no place. The platform's rule (`MapMarks.namesAPlace`).
 */
export function namesAPlace(text: string | undefined, def: LabelContext): boolean {
  const t = (text ?? '').trim();
  if (!t || t === 'Objectiu' || t.startsWith('Zona ')) return false;
  const holds = (s: string | undefined) => !!s && s.length > t.length && s.includes(t);
  return (
    (def.objectives ?? []).some((o) => holds(o.title)) ||
    (def.markers ?? []).some((m) => m.id.startsWith('obj_') && holds(m.text)) ||
    (def.civilians ?? []).some((z) => (z.label ?? '').trim().toLowerCase() === t.toLowerCase()) ||
    (def.zones ?? []).some((z) => (z.label ?? '').endsWith(`: ${t}`))
  );
}

/** The mark of a secondary place with nothing to do at it that the players are told of. */
export const PLACE_OF_INTEREST = 'Lloc d’interès';

/**
 * What must be done at a place, in a word or two, by the kind of the
 * objective that stands there: the text of the yellow mark of a secondary
 * place (the owner, 2026-10-12: the task's name, never the place's). The
 * platform's table (`MapMarks.taskName`).
 */
export function taskName(objectiveType: string | undefined): string {
  switch (objectiveType) {
    case 'retrieve_intel':
      return 'Documents';
    case 'find_cache':
    case 'destroy_cache':
      return 'Dipòsit';
    case 'destroy_installation':
      return 'Instal·lació';
    case 'sabotage_vehicles':
      return 'Vehicles';
    case 'capture_hvt':
      return 'Oficial';
    case 'rescue_hostage':
      return 'Ostatges';
    case 'defuse_bomb':
      return 'Explosiu';
    case 'observe_position':
      return 'Observació';
    case 'clear_area':
    case 'seize_outpost':
      return 'Neteja';
    case 'defend_position':
      return 'Defensa';
    case 'ambush_convoy':
    case 'ambush_reaction':
      return 'Emboscada';
    case 'secure_hlz':
      return 'Zona d’aterratge';
    case 'clear_route':
      return 'Ruta';
    default:
      return 'Objectiu secundari';
  }
}

/**
 * The text of an icon as the map writes it. The secondary place's mark
 * (`secondary_place`) that still carries its place's bare name — a mission
 * stored before the generator wrote the task — reads the task of the first
 * secondary objective the players are told of, never the way out, or a place
 * of interest on a night with none (`MapMarks.secondaryName`, and the
 * publisher's rename). Every other icon keeps its text.
 */
export function iconLabel(marker: { id: string; text?: string }, def: LabelContext): string {
  const text = marker.text ?? '';
  if (marker.id !== 'secondary_place' || !namesAPlace(text, def)) return text;
  const task = (def.objectives ?? []).find(
    (o) => o.priority === 'secondary' && !o.silent && o.type !== 'exfiltrate',
  );
  return task ? taskName(task.type) : PLACE_OF_INTEREST;
}

/**
 * The label of a rect or an ellipse marker as the map writes it. An
 * objective's area (`objective_area`, `obj_<id>_area`) that carries the bare
 * name of a place is labelled with what it is instead — «el castell» on the
 * edge of a circle 200 m from the castle read as a second, misplaced name of
 * it (test card 110, 2026-10-12). The generator writes the kind itself, and
 * the publisher renames a stored mission's; this is the same rule for a
 * definition the web shows as it was stored. Any other shape keeps its text.
 */
export function areaLabel(
  marker: { id: string; text?: string; meaning?: string },
  def: LabelContext,
): string {
  const text = marker.text ?? '';
  const objective =
    marker.meaning === 'objective' ||
    (!marker.meaning &&
      (marker.id === 'objective_area' ||
        (marker.id.startsWith('obj_') && marker.id.endsWith('_area'))));
  if (!objective || !namesAPlace(text, def)) return text;
  const told = (def.objectives ?? []).filter((o) => !o.silent);
  const own =
    (def.objectives ?? []).find((o) => marker.id === `obj_${o.id}_area`) ??
    (marker.id === 'objective_area'
      ? (told.find((o) => o.priority === 'main') ?? told[0])
      : undefined);
  return areaName(own?.type);
}

/** A marker brush as a class of the stylesheet: an outline, a hatch, a grid or a fill. */
export function brushClass(brush: string | undefined): string {
  switch (brush) {
    case 'Border':
      return 'brush-border';
    case 'FDiagonal':
    case 'BDiagonal':
    case 'Horizontal':
    case 'Vertical':
      return `brush-hatch brush-${brush.toLowerCase()}`;
    case 'DiagGrid':
    case 'Cross':
    case 'Grid':
      return 'brush-grid';
    default:
      return 'brush-solid';
  }
}

/**
 * Where a shape's label stands: the point of its edge at compass bearing
 * `edge`, the shape turned by its own angle, clockwise. On a rectangle a
 * bearing between two sides is the corner between them. The same point as
 * `MapMarks.labelAt` and the runtime's `fn_markLabelAt`.
 */
export function labelAt(
  shape: 'rect' | 'ellipse',
  centre: number[],
  a: number,
  b: number,
  angle: number,
  edge: number,
): [number, number] {
  const s = Math.sin((edge * Math.PI) / 180);
  const c = Math.cos((edge * Math.PI) / 180);
  let u = 0;
  let v = 0;
  if (shape === 'ellipse') {
    u = a * s;
    v = b * c;
  } else {
    if (Math.abs(s) > 0.38) u = a * Math.sign(s);
    if (Math.abs(c) > 0.38) v = b * Math.sign(c);
  }
  const t = (angle * Math.PI) / 180;
  return [
    (centre[0] ?? 0) + u * Math.cos(t) + v * Math.sin(t),
    (centre[1] ?? 0) - u * Math.sin(t) + v * Math.cos(t),
  ];
}
